import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Phone, 
  CheckCircle2, 
  FileText, 
  Clock, 
  DollarSign,
  Scale,
  Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const services = [
  {
    icon: FileText,
    title: "Foreclosure Surplus Recovery",
    description: "We help California homeowners recover excess funds from foreclosure sales when the property sold for more than was owed."
  },
  {
    icon: Scale,
    title: "Legal Document Handling",
    description: "We prepare and process all necessary legal documentation required by California counties for fund release."
  },
  {
    icon: Clock,
    title: "Timeline Management",
    description: "California has strict deadlines for claiming surplus funds. We ensure all filings are submitted within required timeframes."
  },
  {
    icon: DollarSign,
    title: "No Upfront Costs",
    description: "We work on contingency — you only pay if we successfully recover your funds. No hidden fees or surprise charges."
  }
];

const benefits = [
  "Expert knowledge of California surplus fund laws",
  "Experience with all 58 California counties",
  "Direct communication throughout the process",
  "Comprehensive document preparation",
  "No recovery, no fee guarantee",
  "Free initial consultation"
];

export default function CaliforniaPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-200/30 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Building2 className="w-4 h-4" />
                California Services
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6" data-testid="california-title">
                Cornerstone <span className="text-amber-600">Solution</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Helping California property owners recover surplus funds from foreclosure sales. 
                Our team understands California's unique county requirements and timelines.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/contact?state=california">
                  <Button className="btn-primary w-full sm:w-auto bg-amber-600 hover:bg-amber-700" data-testid="california-get-started">
                    Start Your Claim
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:405-754-2183">
                  <Button variant="outline" className="btn-secondary w-full sm:w-auto" data-testid="california-call">
                    <Phone className="mr-2 w-5 h-5" />
                    Free Consultation
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl font-bold text-amber-600">CA</span>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">California Surplus Fund Recovery</h3>
                </div>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-amber-600 font-medium uppercase tracking-wider text-sm">
              What We Offer
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
              Our California Services
            </motion.h2>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {services.map((service, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="card-hover border-slate-100 h-full" data-testid={`california-service-${index}`}>
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-amber-50 rounded-xl flex items-center justify-center mb-6">
                      <service.icon className="w-7 h-7 text-amber-600" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Understanding California Surplus Funds
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 leading-relaxed mb-4">
                  When a property in California is sold at a foreclosure auction for more than the 
                  outstanding debt, the excess funds — known as surplus or overage funds — legally 
                  belong to the former property owner.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Many California homeowners are unaware that these funds exist or don't know how to 
                  claim them. Each of California's 58 counties has its own procedures and timelines 
                  for releasing surplus funds.
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  At Cornerstone Solution, we have extensive experience navigating California's county 
                  systems. We handle all the paperwork, meet all deadlines, and keep you informed 
                  every step of the way.
                </p>
              </div>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                <p className="text-slate-700 font-medium mb-2">Important Note:</p>
                <p className="text-slate-600 text-sm">
                  California has specific time limits for claiming surplus funds. Don't wait — 
                  contact us today to ensure you don't miss your deadline.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="bg-slate-900 text-white border-0">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold mb-6">Start Your California Claim</h3>
                  <p className="text-slate-300 mb-8">
                    Ready to see if you have surplus funds waiting? Contact us for a free, 
                    no-obligation consultation.
                  </p>
                  
                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-3 text-slate-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      <span>Free case evaluation</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      <span>No upfront costs</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      <span>Experienced California team</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    <Link to="/contact?state=california">
                      <Button className="w-full bg-amber-600 hover:bg-amber-700" data-testid="california-contact-btn">
                        Contact Us Today
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                    <a href="tel:405-754-2183" className="text-center">
                      <span className="text-slate-400 text-sm">Or call us at </span>
                      <span className="text-white font-medium">405-754-2183</span>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
